import React, { useState } from 'react';
import { BiTrash, BiCopy, BiPencil, BiChevronDown, BiChevronUp, BiMoveHorizontal } from 'react-icons/bi';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Button } from '../ui';
import QuestionCard from './QuestionCard';
import InsertAnchor from './InsertAnchor';

const FieldList = ({
  fields,
  sectionId,
  selectedFieldId,
  renderQuestionCard,
  onSelectField,
  onDuplicateField,
  onDeleteField,
  onCopyField,
  onMoveField,
  onInsertQuestion,
  onInsertSubsection,
  insertOptions = ['question'],
  sortableFieldIds = [],
}) => {
  const renderField = (field, i) => (
    renderQuestionCard ? renderQuestionCard(field, i) : (
      <QuestionCard
        key={field.id}
        field={field}
        index={i}
        isSelected={selectedFieldId === field.id}
        onSelect={() => onSelectField(field)}
        onDuplicate={onDuplicateField}
        onDelete={onDeleteField}
        onCopy={onCopyField}
        onMove={onMoveField}
      />
    )
  );

  const anchorProps = (anchorId, prevFieldId, options = insertOptions) => ({
    anchorId,
    options,
    onInsertQuestion: () => onInsertQuestion?.(sectionId, prevFieldId),
    onInsertSubsection: options.includes('subsection')
      ? () => onInsertSubsection?.(sectionId, prevFieldId)
      : undefined,
  });

  if (!fields?.length) {
    const emptyOptions = insertOptions.includes('subsection')
      ? insertOptions
      : [...insertOptions, ...(onInsertSubsection ? ['subsection'] : [])];
    return (
      <div className="builder-field-list builder-field-list--empty" role="list">
        <InsertAnchor {...anchorProps(`section-${sectionId}-empty`, 0, emptyOptions)} isActive />
      </div>
    );
  }

  const trailingOptions = insertOptions.includes('subsection')
    ? insertOptions
    : [...insertOptions, ...(onInsertSubsection ? ['subsection'] : [])];

  return (
    <SortableContext items={sortableFieldIds} strategy={verticalListSortingStrategy}>
      <div className="builder-field-list" role="list">
        <InsertAnchor {...anchorProps(`section-${sectionId}-start`, 0, ['question'])} />
        {fields.map((field, i) => (
          <React.Fragment key={field.id ?? `field-${i}`}>
            {renderField(field, i)}
            <InsertAnchor
              {...anchorProps(
                `section-${sectionId}-after-${field.id}`,
                field.id,
                i === fields.length - 1 ? trailingOptions : ['question'],
              )}
            />
          </React.Fragment>
        ))}
      </div>
    </SortableContext>
  );
};

const SectionCard = ({
  section,
  fields,
  sectionId = 0,
  selectedFieldId,
  isSectionSelected = false,
  forceExpanded = false,
  onSelectField,
  onEditSection,
  onDeleteSection,
  onDuplicateSection,
  onDuplicateField,
  onDeleteField,
  onCopyField,
  onMoveField,
  onMoveSection,
  isSubsection,
  renderQuestionCard,
  onInsertQuestion,
  onInsertSubsection,
  insertFieldOptions = ['question'],
  renderAfterBody,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const isCollapsed = forceExpanded ? false : collapsed;

  const fieldInsertOptions = ['question'];

  const getSortableIds = (fieldList) => (fieldList || []).map((f, i) => f.id ?? `field-fallback-${i}`);

  if (!section?.sectionAvailable && fields) {
    return (
      <div
        className={`builder-section-card builder-section-card--root${isSectionSelected ? ' builder-section-card--selected' : ''}`}
        data-section-id={sectionId}
      >
        <div className="builder-section-card__header">
          <span className="builder-section-card__title">Questions</span>
          <span className="builder-section-card__count">{fields.length}</span>
        </div>
        <div className="builder-section-card__body">
          <FieldList
            fields={fields}
            sectionId={sectionId}
            selectedFieldId={selectedFieldId}
            renderQuestionCard={renderQuestionCard}
            onSelectField={onSelectField}
            onDuplicateField={onDuplicateField}
            onDeleteField={onDeleteField}
            onCopyField={onCopyField}
            onMoveField={onMoveField}
            onInsertQuestion={onInsertQuestion}
            insertOptions={insertFieldOptions}
            sortableFieldIds={getSortableIds(fields)}
          />
        </div>
      </div>
    );
  }

  if (!section) return null;

  return (
    <div
      className={`builder-section-card ${isCollapsed ? 'builder-section-card--collapsed' : ''} ${isSubsection ? 'builder-section-card--subsection' : ''}${isSectionSelected ? ' builder-section-card--selected' : ''}`}
      data-section-id={section.id}
    >
      <div className="builder-section-card__header">
        <span className="builder-section-card__drag" aria-hidden="true">⋮⋮</span>
        <span className="builder-section-card__title">{section.title}</span>
        <span className="builder-section-card__count">{section.fields?.length || 0} questions</span>
        <div className="builder-section-card__actions">
          {onMoveSection && (() => {
            const blockedBySubsections = !isSubsection && section.subSections?.length > 0;
            return (
              <Button
                variant="ghost"
                size="sm"
                icon={<BiMoveHorizontal />}
                aria-label="Move to section"
                title={blockedBySubsections ? 'Cannot move: this section contains subsections' : 'Move to'}
                disabled={blockedBySubsections}
                onClick={(e) => { e.stopPropagation(); onMoveSection(section); }}
              />
            );
          })()}
          <Button variant="ghost" size="sm" icon={isCollapsed ? <BiChevronDown /> : <BiChevronUp />} aria-label={isCollapsed ? 'Expand' : 'Collapse'} onClick={() => setCollapsed(!collapsed)} />
          <Button variant="ghost" size="sm" icon={<BiPencil />} aria-label="Edit section" onClick={onEditSection} />
          {/* <Button variant="ghost" size="sm" icon={<BiCopy />} aria-label="Duplicate section" title="Duplicate" onClick={(e) => { e.stopPropagation(); onDuplicateSection?.(); }} /> */}
          <Button variant="ghost" size="sm" icon={<BiTrash />} aria-label="Delete section" onClick={onDeleteSection} />
        </div>
      </div>
      {!isCollapsed && (
        <div className="builder-section-card__body">
          <FieldList
            fields={section.fields}
            sectionId={section.id}
            selectedFieldId={selectedFieldId}
            renderQuestionCard={renderQuestionCard}
            onSelectField={onSelectField}
            onDuplicateField={onDuplicateField}
            onDeleteField={onDeleteField}
            onCopyField={onCopyField}
            onMoveField={onMoveField}
            onInsertQuestion={onInsertQuestion}
            onInsertSubsection={onInsertSubsection}
            insertOptions={fieldInsertOptions}
            sortableFieldIds={getSortableIds(section.fields)}
          />
          {renderAfterBody?.()}
        </div>
      )}
    </div>
  );
};

export default SectionCard;
